import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  Typography,
  Stack,
  Paper,
  Tooltip,
} from "@mui/material";
import FormDialog from "components/Dialog/modal-popup";
import CategoryInfoDetails from "./CategoryInfoDetails";
import TitleDefinition from "components/TitleDefinition";
import { LocationModel } from "object-models/location-model";
import { _getLocation, _downloadLocationTemplate } from "middlewares/LocationApi/location";
import CreateUpdateLocation from "pages/CreateUpdateLocation";
import {
  Constants,
  Currency,
  TitleDefinitionConstants,
  UserRoles,
} from "constants/constant";
import AddGrowthRate from "./AddGrowthRate";
import { AlertsProps } from "components/Alerts/data/Alerts.type";
import SnackBar from "components/SnackBar";
import { useAppState } from "context/appState.context";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Utilities } from "utilities/utilities";

/**
 * Component to display the details of an location.
 *
 * @returns {JSX.Element} The rendered component.
 */
const LocationDetails = () => {
  const {
    userRole,
    setLocationName,
    setDiscountFactor,
    setAverageAnnualGrowthRate,
    setCurrency: updateCurrency,
  } = useAppState();
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [locationDetails, setLocationDetails] = useState<LocationModel | null>(
    null,
  );
  const [isEdit, setIsEdit] = useState(false);
  const [isGrowthRate, setIsGrowthRate] = useState(false);
  const { LocationId } = useParams();
  const [success, setSuccess] = useState<AlertsProps>({
    show: false,
    severity: "info",
    message: "",
  });
  const [isEditingInline, setIsEditingInline] = useState(false);
  const UNIT_TCO2E = Constants.UNIT_TCO2E;

  const parsedLocationId = LocationId !== undefined ? Number(LocationId) : NaN;
  const isLocationIdValid = !isNaN(parsedLocationId) && parsedLocationId > 0;

  const handleExportTemplate = async () => {
    if (!isLocationIdValid) {
      setSuccess({
        show: true,
        severity: "error",
        message: "Invalid or missing Location ID. Cannot download template.",
      });
      return;
    }
    try {
      const blob = await _downloadLocationTemplate(parsedLocationId);
      if (blob) {
        const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
        Utilities.triggerFileDownload(blob, `Location_${parsedLocationId}_${today}.xlsx`);
      }
    } catch (err) {
      console.error("Error downloading template:", err);
      setSuccess({
        show: true,
        severity: "error",
        message: "Failed to download template. Please try again.",
      });
    }
  };
  /**
   * Handles the closing of the edit dialog and fetches updated location details.
   */
  function hideEditDialogHandler() {
    hideDialogHandler();
    fetchLocationDetails();
  }

  /**
   * Closes the dialog.
   */
  function hideDialogHandler() {
    setDialogOpen(false);
  }

  /**
   * Handles closing the snackbar by updating its state.
   *
   * @param {AlertsProps} state - The state to set for the snackbar.
   */
  const handleCloseSnackBar = (state: AlertsProps) => {
    setSuccess(state);
  };

  /**
   * Calculates the total baseline emissions.
   *
   * @returns {number | string} The total baseline emissions or "0" if no details are available.
   */
  const calculateTotalBaselineEmissions = (): number | string => {
    if (locationDetails) {
      const { scope1GHGEmission, scope2GHGEmission, scope3GHGEmission } =
        locationDetails;
      const totalBaselineEmissions = (
        Number(scope1GHGEmission ?? 0) +
        Number(scope2GHGEmission ?? 0) +
        Number(scope3GHGEmission ?? 0)
      ).toFixed(2);

      return totalBaselineEmissions;
    }
    return 0;
  };

  /**
   * Fetches location details from the API.
   */
  const fetchLocationDetails = async () => {
    try {
      setLoading(true);
      const res = await _getLocation(Number(LocationId));
      if (res) {
        setLocationDetails(res);
        const name = res?.locationName ?? "";
        setLocationName(name);
        localStorage.setItem("breadcrumb_locationName", name);
        setDiscountFactor(res?.discountFactor ?? 0);
        setAverageAnnualGrowthRate(res?.averageAnnualGrowthRate ?? 0);
        updateCurrency(
          Currency.currency.some((currency) => currency.value === res?.currency)
            ? (res?.currency ?? "")
            : "",
        );
      }
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocationDetails();
  }, [LocationId]);



  let childComponent = null;
  if (isEdit) {
    childComponent = (
      <CreateUpdateLocation isEdit={true} closeDialog={hideEditDialogHandler} />
    );
  } else if (isGrowthRate) {
    childComponent = (
      <AddGrowthRate
        baselineTotalEmissions={calculateTotalBaselineEmissions()}
        locationDetails={locationDetails}
        closeDialog={hideDialogHandler}
        fetchLocationDetails={fetchLocationDetails}
        handleCloseSnackBar={handleCloseSnackBar}
      />
    );
  } else {
    childComponent = <CategoryInfoDetails locationDetails={locationDetails} />;
  }

  return (
    <React.Fragment>
      <FormDialog
        dialogOpen={dialogOpen}
        hideDialogHandler={hideDialogHandler}
        dialogTitle={dialogTitle}
        isFullScreenAllowed={true}
        maxWidth="lg"
        childComponent={childComponent}
      ></FormDialog>

      {/* Location Details in Readonly Mode */}
      {!loading && locationDetails && (
        <Box sx={{ mt: -2 }}>
          {/* Metrics Section - Compact Design */}
          <Paper variant="outlined" sx={{ p: 1.5, mr: 2 ,background:"rgba(228, 97, 15, 0.1) !important"}} >
            {/* Header */}
            <Typography
              sx={{
                color: "#d35400",
                fontWeight: 600,
                fontSize: "14px",
                mb: 1,
              }}
            >
              Baseline GHG Emissions
            </Typography>

            {/* Scopes Row */}
            <Grid container spacing={3} alignItems="flex-start">
              <Grid item xs={2}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography sx={{ fontSize: "12px", color: "#333" }}>
                    Scope 1 ({UNIT_TCO2E})
                  </Typography>
                  <TitleDefinition
                    content={
                      TitleDefinitionConstants.LocationDetails.BaselineScope1
                    }
                    placement="top"
                  />
                </Stack>
                <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                  {locationDetails.scope1GHGEmission?.toLocaleString("en-US") ??
                    "-"}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography sx={{ fontSize: "12px", color: "#333" }}>
                    Scope 2 ({UNIT_TCO2E})
                  </Typography>
                  <TitleDefinition
                    content={
                      TitleDefinitionConstants.LocationDetails.BaselineScope2
                    }
                    placement="top"
                  />
                </Stack>
                <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                  {locationDetails.scope2GHGEmission?.toLocaleString("en-US") ??
                    "-"}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography sx={{ fontSize: "12px", color: "#333" }}>
                    Scope 3 ({UNIT_TCO2E})
                  </Typography>
                  <TitleDefinition
                    content={
                      TitleDefinitionConstants.LocationDetails.BaselineScope3
                    }
                    placement="top"
                  />
                </Stack>
                <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                  {locationDetails.scope3GHGEmission?.toLocaleString("en-US") ??
                    "-"}
                </Typography>
              </Grid>
               <Grid item xs={2.5}>
                <Typography sx={{ fontSize: "12px", color: "#333" }}>
                  Total Baseline Emissions ({UNIT_TCO2E})
                </Typography>
                <Typography
                  sx={{ fontSize: "14px", fontWeight: 600 }}
                  data-testid="totalemission"
                >
                  {Number(calculateTotalBaselineEmissions()).toLocaleString(
                    "en-US",
                    { maximumFractionDigits: 2 },
                  )}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography sx={{ fontSize: "12px", color: "#333" }}>
                  Average Annual Growth Rate
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                    {locationDetails?.averageAnnualGrowthRate == null ? "0%" : locationDetails.averageAnnualGrowthRate + "%"}
                  </Typography>
                  {userRole !== UserRoles.Viewers && (
                    <Button
                      data-testid="averageAnnualGrowthRate"
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setDialogOpen(true);
                        setDialogTitle("Add Growth Rate");
                        setIsGrowthRate(true);
                        setIsEdit(false);
                      }}
                      sx={{
                        fontSize: "10px",
                        textTransform: "uppercase",
                        color: "#d35400",
                        borderColor: "#d35400",
                        py: 0,
                        px: 1,
                        minWidth: "auto",
                        "&:hover": {
                          borderColor: "#d35400",
                          backgroundColor: "rgba(211, 84, 0, 0.04)",
                        },
                      }}
                    >
                      {locationDetails.averageAnnualGrowthRate != null
                        ? "Update"
                        : "Add"}
                    </Button>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={1.5}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography sx={{ fontSize: "12px", color: "#333" }}>
                    Target Baseline Year
                  </Typography>
                  {/* <TitleDefinition
                    content={
                      TitleDefinitionConstants.LocationDetails.BaselineYear
                    }
                    placement="top"
                  /> */}
                </Stack>
                <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                  {locationDetails.baseYear ?? "-"}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Historical Emission Data Section */}
          <Paper variant="outlined" sx={{ mt: 1, p: 1.5, mr: 2 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Typography
                sx={{
                  color: "#d35400",
                  fontWeight: 600,
                  fontSize: "14px",
                }}
              >
                Location Details
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Tooltip title={isLocationIdValid ? "Export Template" : "Export unavailable: invalid location"}>
                  <span>
                    <Button
                      variant="outlined"
                      aria-label="Export Template"
                      data-testid="ExportLocationTemplateButton"
                      onClick={handleExportTemplate}
                      disabled={!isLocationIdValid}
                      size="small"
                      sx={{
                        minWidth: 0,
                        px: 1,
                        color: "#d35400",
                        borderColor: "#d35400",
                        "&:hover": {
                          borderColor: "#d35400",
                          backgroundColor: "rgba(211, 84, 0, 0.04)",
                        },
                      }}
                    >
                      <FileDownloadOutlinedIcon fontSize="small" />
                    </Button>
                  </span>
                </Tooltip>
                {!isEditingInline && userRole !== UserRoles.Viewers && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setIsEditingInline(true)}
                    sx={{
                      fontSize: "11px",
                      textTransform: "uppercase",
                      color: "#d35400",
                      borderColor: "#d35400",
                      "&:hover": {
                        borderColor: "#d35400",
                        backgroundColor: "rgba(211, 84, 0, 0.04)",
                      },
                    }}
                  >
                    Edit Location
                  </Button>
                )}
              </Stack>
            </Stack>
            <CreateUpdateLocation
              isEdit={true}
              readonly={!isEditingInline}
              closeDialog={() => {
                setIsEditingInline(false);
                fetchLocationDetails();
              }}
              showSaveButton={isEditingInline}
            />
          </Paper>
        </Box>
      )}

      <SnackBar
        show={success.show}
        message={success.message}
        severity={success.severity}
        onclose={() =>
          handleCloseSnackBar({ show: false, severity: "info", message: "" })
        }
      />
    </React.Fragment>
  );
};

export default LocationDetails;
